import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader, EmptyState } from "@/components/common/Loader";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadCloud, Search, Check } from "lucide-react";

// Media library + uploader. Calls onSelect({ url, alt, id }) when an image is picked.
export const MediaPicker = ({ open, onOpenChange, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      setItems((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. The previous image was kept.");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const filtered = items.filter((m) => (m.original_filename || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="media-picker" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="display-title text-2xl">Media Library</DialogTitle>
          <DialogDescription>Upload a new image or pick an existing one. Images are optimized automatically.</DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          data-testid="media-dropzone"
          className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-[hsl(var(--brand-red))] bg-brand/5" : "border-border"}`}
        >
          <UploadCloud className="h-8 w-8 text-brand" />
          <p className="text-sm text-muted-foreground">Drag &amp; drop an image here, or</p>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="media-upload-button" className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60">
            {uploading ? "Uploading…" : "Browse files"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="media-file-input" />
        </div>

        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search images…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="media-search" />
        </div>

        <div className="mt-2 max-h-[340px] overflow-y-auto">
          {loading ? <Loader /> : filtered.length === 0 ? <EmptyState title="No images yet" description="Upload your first image above." /> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {filtered.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onSelect({ url: m.url, alt: m.alt || "", id: m.id }); onOpenChange(false); }}
                  data-testid={`media-item-${m.id}`}
                  className="group relative overflow-hidden rounded-lg border border-border transition-colors hover:border-[hsl(var(--brand-red))]"
                >
                  <img src={m.url} alt={m.alt || m.original_filename} className="aspect-square w-full object-cover" />
                  <span className="absolute inset-0 hidden items-center justify-center bg-black/40 group-hover:flex"><Check className="h-6 w-6 text-white" /></span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
